import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  policies,
  type ListParams,
  type Policy,
  type PolicyPendingIssuance,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { baixarCsv, moedaParaPlanilha } from '@/shared/lib/exportar';
import { formatCivilDate, formatDate, formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { CopyButton } from '@/shared/ui/CopyButton';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useToast } from '@/shared/ui/useToast';

import { EndorsementRequestDialog } from '../aftercare/EndorsementRequestDialog';
import { PartyToolbar } from '../shared/PartyToolbar';

import {
  contaFiltros,
  corretorDaApolice,
  DICA_DA_VIGENCIA,
  documentoOuVazio,
  filtrosDeApoliceVazios,
  NAO_DEFINIDO,
  numeroDaApolice,
  ouNaoDefinido,
  pendentesEntram,
  seguradoDaApolice,
  simNao,
  tomadorDaApolice,
  vigenciaDe,
  type PolicyFilters,
} from './apolice';
import { BilletsDialog } from './BilletsDialog';
import { NewEndorsementDialog } from './NewEndorsementDialog';
import { PolicyDownloadsMenu } from './PolicyDownloadsMenu';
import { PolicyFiltersDialog } from './PolicyFiltersDialog';
import { PolicySettingsDialog } from './PolicySettingsDialog';

const limite = 25;

/** Uma linha da lista: a apolice emitida, ou a operacao que aguarda emissao ("S/N"). */
type Linha =
  | { tipo: 'apolice'; apolice: Policy }
  | { tipo: 'pendente'; pendente: PolicyPendingIssuance };

function filtrosParaConsulta(filtros: PolicyFilters): ListParams {
  return {
    number: filtros.number.trim(),
    status: filtros.status,
    start_date: filtros.start_date,
    end_date: filtros.end_date,
    coinsurance_type: filtros.coinsurance_type,
    big_risk: filtros.big_risk,
    monitoring: filtros.monitoring,
    step_in: filtros.step_in,
    policyholder_name: filtros.policyholder_name.trim(),
    policyholder_document: filtros.policyholder_document.replace(/\D/g, ''),
    insured_name: filtros.insured_name.trim(),
    insured_document: filtros.insured_document.replace(/\D/g, ''),
    broker_name: filtros.broker_name.trim(),
  };
}

/**
 * Carteira de garantias emitidas (P-APO-1), com as colunas na ordem do cartao do
 * OnPoint: Apolice ("S/N" antes da emissao), Cotacao, Tomador, Segurado, Corretor,
 * Modalidade, IS, Premio da Apolice, Contem endosso?, Contem cosseguro?, Emissao,
 * Vigencia Atual, Status e Ref. da Seguradora, com o botao de copiar nas celulas.
 *
 * A situacao vem do servidor ja resolvida pela data. Os filtros ficam no painel
 * "Apolices" e so valem ao aplicar. As operacoes em emissao entram no comeco da
 * primeira pagina como "Aguardando Emissao", sem apolice: a apolice continua nascendo
 * so na emissao (ADR 0039).
 *
 * Seguradora emite seguro garantia, banco emite fianca bancaria. A tela e a mesma;
 * o corte por emissor vem do servidor.
 */
export function PoliciesPage({
  issuerKind = '',
  titulo = 'Apolices',
  descricao = 'Garantias emitidas, com vigencia e situacao.',
  vazio = 'A carteira se preenche a partir da emissao. Leve uma operacao ate a etapa de emissao para gerar a primeira apolice.',
  vazioTitulo = 'Nenhuma apolice emitida',
}: {
  issuerKind?: 'seguradora' | 'banco' | '';
  titulo?: string;
  descricao?: string;
  vazio?: string;
  vazioTitulo?: string;
} = {}) {
  const { can, session } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const brokerScoped = session?.broker_scoped ?? false;
  const base = issuerKind === 'banco' ? '/fiancas' : '/apolices';

  const [busca, setBusca] = useState('');
  const [filtros, setFiltros] = useState<PolicyFilters>(filtrosDeApoliceVazios);
  const [vencendo, setVencendo] = useState(false);
  const [offset, setOffset] = useState(0);
  const [painel, setPainel] = useState<
    'filtros' | 'configuracao' | 'novo-endosso' | null
  >(null);
  const [boletosDe, setBoletosDe] = useState<Policy | null>(null);
  const [endossoDe, setEndossoDe] = useState<Policy | null>(null);

  const podeEndossar = can('endossos.endosso.create');
  const podeExportar = can(
    issuerKind === 'banco' ? 'fiancas.fianca.export' : 'apolices.apolice.export',
  );
  const podeConfigurar = can('apolices.configuracao.update') && !brokerScoped;

  const consultaBase: ListParams = {
    q: busca,
    ...filtrosParaConsulta(filtros),
    issuer_kind: issuerKind || undefined,
    expiring_in_days: vencendo ? 90 : undefined,
  };

  const soPendentes = filtros.status === 'aguardando_emissao';
  const consulta = useQuery({
    queryKey: ['policies', { ...consultaBase, offset }],
    queryFn: ({ signal }) =>
      policies.list({ ...consultaBase, limit: limite, offset }, signal),
    enabled: !soPendentes,
  });

  // "Aguardando Emissao": as operacoes em emissao, no comeco da primeira pagina. Quem
  // nao le operacao recebe a lista vazia do servidor.
  const mostraPendentes =
    issuerKind !== 'banco' &&
    busca === '' &&
    !vencendo &&
    pendentesEntram(filtros) &&
    (soPendentes || offset === 0);
  const pendentes = useQuery({
    queryKey: [
      'policies-pending',
      { ...consultaBase, offset: soPendentes ? offset : 0 },
    ],
    queryFn: ({ signal }) =>
      policies.pendingIssuance(
        {
          policyholder_name: consultaBase.policyholder_name,
          policyholder_document: consultaBase.policyholder_document,
          broker_name: consultaBase.broker_name,
          big_risk: consultaBase.big_risk,
          monitoring: consultaBase.monitoring,
          step_in: consultaBase.step_in,
          limit: limite,
          offset: soPendentes ? offset : 0,
        },
        signal,
      ),
    enabled: mostraPendentes,
  });

  const exportar = useMutation({
    mutationFn: () => policies.exportAll(consultaBase),
    onSuccess: (itens) =>
      baixarCsv(titulo.toLowerCase(), itens, [
        { titulo: 'Apolice', valor: (item) => numeroDaApolice(item) },
        { titulo: 'Cotacao', valor: (item) => item.proposal_number ?? '' },
        { titulo: 'Tomador', valor: (item) => tomadorDaApolice(item).nome },
        {
          titulo: 'CNPJ do tomador',
          valor: (item) => tomadorDaApolice(item).documento,
        },
        { titulo: 'Segurado', valor: (item) => seguradoDaApolice(item)?.nome ?? '' },
        {
          titulo: 'Corretor',
          valor: (item) => corretorDaApolice(item)?.nome ?? 'direto',
        },
        { titulo: 'Modalidade', valor: (item) => item.modality.label },
        { titulo: 'Emissor', valor: (item) => item.issuer.label },
        { titulo: 'IS', valor: (item) => moedaParaPlanilha(item.insured_amount) },
        { titulo: 'Premio', valor: (item) => moedaParaPlanilha(item.premium) },
        {
          titulo: 'Contem endosso',
          valor: (item) => simNao((item.endorsement_count ?? 0) > 0),
        },
        { titulo: 'Contem cosseguro', valor: (item) => simNao(item.has_coinsurance) },
        { titulo: 'Emissao', valor: (item) => item.issued_at.slice(0, 10) },
        {
          titulo: 'Inicio da vigencia',
          valor: (item) => item.coverage_start.slice(0, 10),
        },
        { titulo: 'Fim da vigencia', valor: (item) => item.coverage_end.slice(0, 10) },
        { titulo: 'Situacao', valor: (item) => statusLabel('apolice', item.status) },
        { titulo: 'Ref. da seguradora', valor: (item) => item.insurer_reference ?? '' },
      ]),
    onError: (error: Error) => toast.error(error.message),
  });

  const linhas: Linha[] = [
    ...(mostraPendentes
      ? (pendentes.data?.items ?? []).map((pendente): Linha => ({
          tipo: 'pendente',
          pendente,
        }))
      : []),
    ...(soPendentes
      ? []
      : (consulta.data?.items ?? []).map((apolice): Linha => ({
          tipo: 'apolice',
          apolice,
        }))),
  ];
  const total = soPendentes
    ? (pendentes.data?.total ?? 0)
    : (consulta.data?.total ?? 0);
  // Consulta desligada fica "pendente" para sempre: so conta a que esta ligada.
  const carregando = soPendentes
    ? mostraPendentes && pendentes.isPending
    : consulta.isPending;
  const filtrando = busca !== '' || vencendo || contaFiltros(filtros) > 0;

  const colunas: Column<Linha>[] = [
    {
      key: 'apolice',
      header: 'Apolice',
      render: (linha) =>
        linha.tipo === 'pendente' ? (
          <span className="font-medium text-ink-500">S/N</span>
        ) : (
          <ComCopia valor={numeroDaApolice(linha.apolice)} rotulo="numero da apolice">
            <span className="font-medium text-ink-900">
              {numeroDaApolice(linha.apolice)}
            </span>
          </ComCopia>
        ),
    },
    {
      key: 'cotacao',
      header: 'Cotacao',
      hideBelow: 'lg',
      render: (linha) => {
        const numero =
          linha.tipo === 'pendente'
            ? linha.pendente.proposal_number
            : (linha.apolice.proposal_number ?? null);
        return numero ? (
          <ComCopia valor={numero} rotulo="numero da cotacao">
            <span className="tabular-nums">{numero}</span>
          </ComCopia>
        ) : (
          <span className="text-ink-400">{NAO_DEFINIDO}</span>
        );
      },
    },
    {
      key: 'tomador',
      header: 'Tomador',
      render: (linha) => {
        const tomador =
          linha.tipo === 'pendente'
            ? {
                nome: linha.pendente.policyholder.label,
                documento: linha.pendente.policyholder_document,
              }
            : tomadorDaApolice(linha.apolice);
        return (
          <ComCopia valor={tomador.documento || tomador.nome} rotulo="tomador">
            <Pessoa
              nome={tomador.nome}
              documento={documentoOuVazio(tomador.documento)}
            />
          </ComCopia>
        );
      },
    },
    {
      key: 'segurado',
      header: 'Segurado',
      hideBelow: 'md',
      render: (linha) => {
        const segurado =
          linha.tipo === 'pendente'
            ? linha.pendente.insured
              ? {
                  nome: linha.pendente.insured.label,
                  documento: linha.pendente.insured.sublabel,
                }
              : null
            : seguradoDaApolice(linha.apolice);
        if (!segurado) return <span className="text-ink-400">{NAO_DEFINIDO}</span>;
        return (
          <ComCopia valor={segurado.documento || segurado.nome} rotulo="segurado">
            <Pessoa
              nome={segurado.nome}
              documento={documentoOuVazio(segurado.documento)}
            />
          </ComCopia>
        );
      },
    },
    {
      key: 'corretor',
      header: 'Corretor',
      hideBelow: 'lg',
      render: (linha) =>
        linha.tipo === 'pendente'
          ? (linha.pendente.broker?.label ?? 'Operacao direta')
          : (corretorDaApolice(linha.apolice)?.nome ?? 'Operacao direta'),
    },
    {
      // O emissor e do SeguraCore, que emite por seguradora e por banco: continua ao
      // lado das colunas do OnPoint.
      key: 'emissor',
      header: 'Emissor',
      hideBelow: 'lg',
      render: (linha) =>
        linha.tipo === 'pendente' ? (
          <span className="text-ink-400">{NAO_DEFINIDO}</span>
        ) : (
          linha.apolice.issuer.label
        ),
    },
    {
      key: 'modalidade',
      header: 'Modalidade',
      hideBelow: 'lg',
      render: (linha) =>
        linha.tipo === 'pendente'
          ? linha.pendente.modality.label
          : linha.apolice.modality.label,
    },
    {
      key: 'valor',
      header: 'IS',
      align: 'right',
      render: (linha) =>
        formatMoney(
          linha.tipo === 'pendente'
            ? linha.pendente.insured_amount
            : linha.apolice.insured_amount,
        ),
    },
    {
      key: 'premio',
      header: 'Premio da apolice',
      align: 'right',
      hideBelow: 'md',
      render: (linha) =>
        formatMoney(
          linha.tipo === 'pendente' ? linha.pendente.premium : linha.apolice.premium,
        ),
    },
    {
      key: 'endosso',
      header: 'Contem endosso?',
      hideBelow: 'lg',
      render: (linha) =>
        linha.tipo === 'pendente'
          ? '--'
          : simNao((linha.apolice.endorsement_count ?? 0) > 0),
    },
    {
      key: 'cosseguro',
      header: 'Contem cosseguro?',
      hideBelow: 'lg',
      render: (linha) =>
        linha.tipo === 'pendente' ? '--' : simNao(linha.apolice.has_coinsurance),
    },
    {
      key: 'emissao',
      header: 'Emissao',
      hideBelow: 'md',
      render: (linha) =>
        linha.tipo === 'pendente' ? (
          <span className="text-ink-400">{NAO_DEFINIDO}</span>
        ) : (
          formatDate(linha.apolice.issued_at)
        ),
    },
    {
      key: 'vigencia',
      header: 'Vigencia atual',
      help: DICA_DA_VIGENCIA,
      render: (linha) =>
        linha.tipo === 'pendente' ? (
          vigenciaDe(linha.pendente.coverage_start, linha.pendente.coverage_end)
        ) : (
          <VigenciaCelula policy={linha.apolice} />
        ),
    },
    {
      key: 'situacao',
      header: 'Status',
      render: (linha) => (
        <StatusBadge
          kind="apolice"
          value={
            linha.tipo === 'pendente' ? 'aguardando_emissao' : linha.apolice.status
          }
        />
      ),
    },
    {
      key: 'referencia',
      header: 'Ref. da seguradora',
      hideBelow: 'lg',
      render: (linha) => {
        if (linha.tipo === 'pendente')
          return <span className="text-ink-400">{NAO_DEFINIDO}</span>;
        const referencia = linha.apolice.insurer_reference ?? '';
        return referencia ? (
          <ComCopia valor={referencia} rotulo="referencia da seguradora">
            {referencia}
          </ComCopia>
        ) : (
          <span className="text-ink-400">{NAO_DEFINIDO}</span>
        );
      },
    },
    {
      key: 'acoes',
      header: 'Acoes',
      render: (linha) =>
        linha.tipo === 'pendente' ? (
          <Link
            to={`/operacoes/${linha.pendente.operation_id}`}
            className="text-sm text-brand-600 underline-offset-2 hover:underline"
          >
            Ver a cotacao
          </Link>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <PolicyDownloadsMenu
              policy={linha.apolice}
              onBillets={() => setBoletosDe(linha.apolice)}
            />
            <Link
              to={`${base}/${linha.apolice.id}`}
              className="text-sm text-brand-600 underline-offset-2 hover:underline"
            >
              Detalhes da apolice
            </Link>
            {podeEndossar && linha.apolice.status === 'vigente' ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEndossoDe(linha.apolice)}
              >
                Solicitar endosso
              </Button>
            ) : null}
          </div>
        ),
    },
  ];

  const quantos = contaFiltros(filtros);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={titulo}
        description={descricao}
        actions={
          <div className="flex flex-wrap items-end gap-2">
            {issuerKind !== 'banco' && can('operacoes.operacao.view') ? (
              <div className="w-48">
                <Select
                  label="Lista"
                  value="apolices"
                  options={[
                    { value: 'propostas', label: 'Lista de propostas' },
                    { value: 'apolices', label: 'Lista de apolices' },
                  ]}
                  onChange={(event) => {
                    if (event.target.value === 'propostas') void navigate('/operacoes');
                  }}
                />
              </div>
            ) : null}
            <Button variant="secondary" onClick={() => setPainel('filtros')}>
              {quantos > 0 ? `Filtros (${quantos})` : 'Filtros'}
            </Button>
            {podeEndossar && issuerKind !== 'banco' ? (
              <Button variant="secondary" onClick={() => setPainel('novo-endosso')}>
                Novo endosso
              </Button>
            ) : null}
            {podeConfigurar ? (
              <Button variant="secondary" onClick={() => setPainel('configuracao')}>
                Configuracao da apolice
              </Button>
            ) : null}
            {podeExportar ? (
              <Button
                variant="secondary"
                loading={exportar.isPending}
                disabled={total === 0}
                onClick={() => exportar.mutate()}
              >
                Exportar
              </Button>
            ) : null}
          </div>
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a carteira">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por numero da apolice"
          extra={
            <label className="flex h-9 cursor-pointer items-center gap-2 self-end text-xs text-ink-600">
              <input
                type="checkbox"
                checked={vencendo}
                onChange={(event) => {
                  setVencendo(event.target.checked);
                  setOffset(0);
                }}
                className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
              />
              Vence em ate noventa dias
            </label>
          }
        />

        <DataTable
          columns={colunas}
          items={linhas}
          keyOf={(linha) =>
            linha.tipo === 'pendente'
              ? `pendente-${linha.pendente.operation_id}`
              : linha.apolice.id
          }
          loading={carregando}
          empty={
            <EmptyState
              icon="shield"
              title={
                filtrando
                  ? 'Nenhuma apolice encontrada com os filtros atuais.'
                  : vazioTitulo
              }
              description={
                filtrando
                  ? filtros.start_date !== ''
                    ? 'Recomendamos ajustar o filtro de datas para um periodo mais amplo.'
                    : 'Recomendamos alterar os filtros aplicados para obter resultados.'
                  : vazio
              }
            />
          }
        />

        <Pagination total={total} limit={limite} offset={offset} onChange={setOffset} />
      </Card>

      <PolicyFiltersDialog
        open={painel === 'filtros'}
        value={filtros}
        brokerScoped={brokerScoped}
        onClose={() => setPainel(null)}
        onApply={(novos) => {
          setFiltros(novos);
          setOffset(0);
          setPainel(null);
        }}
      />

      <PolicySettingsDialog
        open={painel === 'configuracao'}
        onClose={() => setPainel(null)}
      />

      <NewEndorsementDialog
        open={painel === 'novo-endosso'}
        onClose={() => setPainel(null)}
      />

      <BilletsDialog
        policy={boletosDe}
        open={boletosDe !== null}
        onClose={() => setBoletosDe(null)}
      />

      {endossoDe ? (
        <EndorsementRequestDialog
          policy={endossoDe}
          open
          onClose={() => setEndossoDe(null)}
        />
      ) : null}
    </div>
  );
}

function ComCopia({
  valor,
  rotulo,
  children,
}: {
  valor: string;
  rotulo: string;
  children: ReactNode;
}) {
  return (
    <span className="flex items-center gap-1">
      <span className="min-w-0">{children}</span>
      <CopyButton value={valor} label={`Copiar ${rotulo}`} />
    </span>
  );
}

function Pessoa({ nome, documento }: { nome: string; documento: string }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="truncate text-ink-900">{ouNaoDefinido(nome)}</span>
      {documento ? (
        <span className="truncate text-2xs text-ink-500 tabular-nums">{documento}</span>
      ) : null}
    </span>
  );
}

/**
 * Vigencia atual com inicio e fim, e os dias que faltam pela data civil.
 */
export function VigenciaCelula({ policy }: { policy: Policy }) {
  const dias = policy.days_to_expiry;
  const tom =
    policy.status === 'cancelada'
      ? 'text-ink-400'
      : dias < 0
        ? 'text-danger-700'
        : dias <= 60
          ? 'text-warning-700'
          : 'text-ink-700';

  return (
    <div className="flex flex-col">
      <span className={cn('text-sm tabular-nums', tom)}>
        {formatCivilDate(policy.coverage_start)} -{' '}
        {formatCivilDate(policy.coverage_end)}
      </span>
      {policy.status !== 'cancelada' ? (
        <span className="text-2xs text-ink-500">
          {dias < 0 ? `vencida ha ${Math.abs(dias)} d` : `${dias} d restantes`}
        </span>
      ) : null}
    </div>
  );
}
